/** Phase E — MSC Finance: payer-specific Payout Runs. No live ACH. */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import useAuthStore from '../../lib/auth-store';
import { Card, PageHeader, TenantSelect, btnPrimary, errorText, inputCls, money, useTenantScope } from '../../components/finance/financeUi';
import { CutoverCard } from '../../components/payer-runs/CutoverCard';
import { RunsTable } from '../../components/payer-runs/RunsTable';
import { RunDetail } from '../../components/payer-runs/RunDetail';
import { HeldLines } from '../../components/payer-runs/HeldLines';
import { DownlineAuthority } from '../../components/payer-runs/DownlineAuthority';

function useTenantData(tenantId) {
  const [cut, setCut] = useState(null);
  const [flags, setFlags] = useState(null);
  const [periods, setPeriods] = useState([]);
  const load = useCallback(() => {
    if (!tenantId) return;
    api.request(`/payer-runs/cutover?tenant_id=${tenantId}`).then(setCut).catch(() => setCut(null));
    api.request(`/payout-routing/tenant-flags?tenant_id=${tenantId}`).then(setFlags).catch(() => setFlags(null));
    api.request(`/payout-routing/periods?tenant_id=${tenantId}`).then((p) => setPeriods([...new Set(p.map((x) => x.period))])).catch(() => setPeriods([]));
  }, [tenantId]);
  useEffect(load, [load]);
  return { cut, flags, periods, reload: load };
}

export default function PayerPayoutRunsPage() {
  const { user } = useAuthStore();
  const scope = useTenantScope(user);
  const { cut, flags, periods, reload } = useTenantData(scope.tenantId);
  const [period, setPeriod] = useState('');
  const [runs, setRuns] = useState([]);
  const [held, setHeld] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const auth = cut?.mode === 'AUTHORITATIVE';
  const choices = auth ? periods.filter((p) => p >= cut.effective_from) : [];
  useEffect(() => { setPeriod(choices[0] || ''); setSelected(null); setSummary(null); }, [scope.tenantId, cut?.mode, cut?.effective_from, periods.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadRuns = useCallback(() => {
    if (!scope.tenantId || !period) { setRuns([]); setHeld([]); return; }
    api.request(`/payer-runs/runs?tenant_id=${scope.tenantId}&period=${period}`).then(setRuns).catch((e) => toast.error(errorText(e)));
    api.request(`/payer-runs/held?tenant_id=${scope.tenantId}&period=${period}`).then(setHeld).catch(() => setHeld([]));
  }, [scope.tenantId, period]);
  useEffect(loadRuns, [loadRuns]);
  useEffect(() => { if (selected) api.request(`/payer-runs/runs/${selected}`).then(setDetail).catch(() => setDetail(null)); else setDetail(null); }, [selected]);
  const build = async () => {
    try { const r = await api.request('/payer-runs/build', { method: 'POST', body: JSON.stringify({ tenant_id: scope.tenantId, period }) }); setSummary(r); toast.success(`${r.runs.length} payer run(s) built`); loadRuns(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="space-y-6" data-testid="payer-payout-runs-page">
      <PageHeader title="Payer Payout Runs" subtitle="Each payer sees only what it owes. MSC pays its direct partners and affiliates; each ISO or approved Agent pays its own downline. Money is not sent from here yet: settlements are recorded by hand.">
        <TenantSelect scope={scope} testid="payer-runs-tenant" />
      </PageHeader>
      {flags?.synthetic && <div className="rounded-xl border-2 border-dashed border-amber-500 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900" data-testid="synthetic-data-banner">Synthetic Demo Data — No Real Payments</div>}
      <Card title="Cutover" testid="cutover-card"><CutoverCard tenantId={scope.tenantId} cut={cut} onChange={reload} /></Card>
      {auth && (
        <Card title="Runs" testid="runs-card" aside={
          <div className="flex items-center gap-2">
            <select className={`${inputCls} w-32`} value={period} onChange={(e) => { setPeriod(e.target.value); setSelected(null); }} data-testid="runs-period">
              {!choices.length && <option value="">No periods</option>}
              {choices.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button type="button" className={`${btnPrimary} whitespace-nowrap`} disabled={!period} onClick={build} data-testid="build-runs-btn">Build / refresh</button>
          </div>}>
          {summary && <p className="border-b px-5 py-3 text-xs" data-testid="build-summary">Step 9 payables {money(summary.step9_total_payables)} (final {money(summary.step9_final_payable_entries)} + affiliate carve-outs {money(summary.step9_affiliate_obligations)}) = MSC direct {money(summary.msc_direct_obligations)} + held {money(summary.held_total)} · {summary.conservation_ok ? 'balanced to the cent' : 'NOT BALANCED'}</p>}
          <RunsTable runs={runs} selected={selected} onSelect={setSelected} />
          {detail && <div className="border-t"><RunDetail run={detail} isMsc onChange={(r) => { setDetail(r); loadRuns(); }} /></div>}
        </Card>
      )}
      {auth && <Card title={`Routing exceptions (${held.length})`} testid="held-card"><HeldLines rows={held} /></Card>}
      <Card title="Downline authority & payout readiness" testid="authority-card"><DownlineAuthority tenantId={scope.tenantId} /></Card>
    </div>
  );
}
